// Tetris game logic

// Tetromino shapes
export const TETROMINOS = {
  I: {
    shape: [
      [0, 0, 0, 0],
      [1, 1, 1, 1],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
    ],
    color: 'hsl(var(--tetromino-i))',
  },
  O: {
    shape: [
      [1, 1],
      [1, 1],
    ],
    color: 'hsl(var(--tetromino-o))',
  },
  T: {
    shape: [
      [0, 1, 0],
      [1, 1, 1],
      [0, 0, 0],
    ],
    color: 'hsl(var(--tetromino-t))',
  },
  S: {
    shape: [
      [0, 1, 1],
      [1, 1, 0],
      [0, 0, 0],
    ],
    color: 'hsl(var(--tetromino-s))',
  },
  Z: {
    shape: [
      [1, 1, 0],
      [0, 1, 1],
      [0, 0, 0],
    ],
    color: 'hsl(var(--tetromino-z))',
  },
  J: {
    shape: [
      [1, 0, 0],
      [1, 1, 1],
      [0, 0, 0],
    ],
    color: 'hsl(var(--tetromino-j))',
  },
  L: {
    shape: [
      [0, 0, 1],
      [1, 1, 1],
      [0, 0, 0],
    ],
    color: 'hsl(var(--tetromino-l))',
  },
};

const TETROMINO_KEYS = Object.keys(TETROMINOS);

// Create an empty game board
export const createEmptyBoard = (rows = 20, cols = 10) => {
  return Array.from({ length: rows }, () => Array(cols).fill(null));
};

// Get a random tetromino
export const randomTetromino = () => {
  const key = TETROMINO_KEYS[Math.floor(Math.random() * TETROMINO_KEYS.length)];
  return {
    type: key,
    shape: TETROMINOS[key].shape,
    color: TETROMINOS[key].color,
  };
};

// Rotate tetromino
export const rotateTetromino = (shape) => {
  const rows = shape.length;
  const cols = shape[0].length;
  const rotated = Array.from({ length: cols }, () => Array(rows).fill(0));

  for (let i = 0; i < rows; i++) {
    for (let j = 0; j < cols; j++) {
      rotated[j][rows - 1 - i] = shape[i][j];
    }
  }

  return rotated;
};

// Check if a position is valid
export const isValidPosition = (board, shape, position) => {
  const { x, y } = position;

  for (let row = 0; row < shape.length; row++) {
    for (let col = 0; col < shape[row].length; col++) {
      if (shape[row][col]) {
        const newY = y + row;
        const newX = x + col;

        // Check boundaries
        if (
          newX < 0 ||
          newX >= board[0].length ||
          newY >= board.length
        ) {
          return false;
        }

        // Check if position is occupied (but allow negative Y for piece spawning)
        if (newY >= 0 && board[newY][newX]) {
          return false;
        }
      }
    }
  }

  return true;
};

// Merge piece into board
export const mergePieceToBoard = (board, shape, position, color) => {
  const newBoard = board.map(row => [...row]);
  const { x, y } = position;

  for (let row = 0; row < shape.length; row++) {
    for (let col = 0; col < shape[row].length; col++) {
      if (shape[row][col]) {
        const newY = y + row;
        const newX = x + col;
        if (newY >= 0) {
          newBoard[newY][newX] = color;
        }
      }
    }
  }

  return newBoard;
};

// Check and clear completed lines
export const clearLines = (board) => {
  let linesCleared = 0;
  const newBoard = [];

  for (let row of board) {
    if (row.every(cell => cell !== null)) {
      linesCleared++;
    } else {
      newBoard.push(row);
    }
  }

  // Add empty rows at the top
  while (newBoard.length < board.length) {
    newBoard.unshift(Array(board[0].length).fill(null));
  }

  return { newBoard, linesCleared };
};

// Calculate score based on lines cleared
export const calculateScore = (linesCleared) => {
  return linesCleared * 10;
};

// Calculate game speed based on level
export const getGameSpeed = (level) => {
  // Cap difficulty level at 15
  const effectiveLevel = Math.min(level, 15);
  return Math.max(100, 800 - (effectiveLevel - 1) * 50);
};

// Check if game is over
export const isGameOver = (board, nextPieceShape, spawnPosition) => {
  // Game is over if:
  // 1. Any block exists in the top row
  if (board[0].some(cell => cell !== null)) return true;

  // 2. OR the next piece can't even be placed at its starting position
  if (nextPieceShape && spawnPosition) {
    return !isValidPosition(board, nextPieceShape, spawnPosition);
  }

  return false;
};